import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form } from 'react-bootstrap';

import { UseOutletContextBankStatement } from './BankStatement';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { BankStatementDA } from '../../../../services/admin/bank.statement.service';
import { BankStatementLine } from '../../../../models/bank.statement.model';
import { TreasuryAccount } from '../../../../models/treasury.account.model';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatMoney, formatAccountingAccount } from '../../../../scripts/format.value.script';
import { modalShow, modalHide } from '../../../../utils/modal.util';
import { GlobalSwal } from '../../../../styles/swal.style';
import { triggerBlobDownload } from '../../../../scripts/export.blob.script';
import { exportFallbackFilename } from '../../../../scripts/document.filename.script';

import AppPreloader from '../../../../components/Preloader';
import DropdownActions from '../../../../components/dropdown/DropdownActions';
import AppElementPagination from '../../../../components/element/ElementPagination';
import AppModalBankLineMatch, { MatchCandidate, UnvalidatedCandidate, PayableCandidate } from '../../../../components/modal/ModalBankLineMatch';
import AppModalProviderInvoiceUpdateValidate from '../../../../components/modal/ModalProviderInvoiceUpdateStatusValidate';
import AppModalExpenseUpdateValidate from '../../../../components/modal/ModalExpenseUpdateStatusValidate';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { Invoice } from '../../../../models/invoice.model';
import { Expense } from '../../../../models/expense.model';
import { AccountingAccount } from '../../../../models/accounting.account.model';
import { Settings } from '../../../../models/settings.model';
import { ProviderInvoiceDA } from '../../../../services/admin/provider.invoice.service';
import { InvoiceDA } from '../../../../services/admin/invoice.service';
import { ExpenseDA } from '../../../../services/admin/expense.service';
import { AccountingAccountDA } from '../../../../services/admin/accounting.account.service';
import { SettingsDA } from '../../../../services/admin/settings.service';
import { ServiceDA } from '../../../../services/admin/service.service';
import { ProductDA } from '../../../../services/admin/product.service';
import { Service } from '../../../../models/service.model';
import { Product } from '../../../../models/product.model';
import { BookingJournalDA } from '../../../../services/admin/booking.journal.service';
import AppModalInvoiceUpdateValidate from '../../../../components/modal/ModalInvoiceUpdateStatusValidate';
import AppModalManualEntry, { ManualEntryBankLine } from '../../../../components/modal/ModalManualEntry';

export interface BankTransactionListPageProps {};

type ReconciliationStatusFilter = 'all' | 'pending' | 'matched' | 'ignored'

interface BankTransaction {
  line_id: string
  id_bank_statement: string
  import_reference: string | null
  id_treasury_account: string
  statement_format: string
  statement_period_start: string
  statement_period_end: string
  date: string
  amount: number
  description: string
  reference: string | null
  iban_counterpart: string | null
  reconciliation_status: 'pending' | 'matched' | 'ignored'
  id_entry: string | null
  id_invoice: string | null
  matched_at: string | null
  matched_via: 'qr_auto' | 'qr_confirmed' | 'heuristic' | 'manual' | 'treasury_link' | null
  match_note: string | null
}

const BankTransactionListPage: React.FunctionComponent<BankTransactionListPageProps> = props => {
  const {setRoute, companyForUser, treasuryAccountsForCompany} = UseOutletContextBankStatement()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [mounted, setMounted] = useState(false)
  const [transactions, setTransactions] = useState<BankTransaction[] | null>(null)
  const [pagination, setPagination] = useState({limit: 100, total: 0, pages: 0})
  const [goToPage, setGoToPage] = useState('')

  const urlPage = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1)
  const urlQ = searchParams.get('q') ?? ''

  const [searchText, setSearchText] = useState(urlQ)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setUrlPage = useCallback((n: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (n <= 1) { next.delete('page') } else { next.set('page', String(n)) }
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setUrlQ = useCallback((q: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (q) { next.set('q', q) } else { next.delete('q') }
      next.delete('page')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const handleSearchChange = (value: string) => {
    setSearchText(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setUrlQ(value), 350)
  }

  const handleSearchClear = () => {
    setSearchText('')
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setUrlQ('')
  }

  useEffect(() => { return () => { if (debounceRef.current) clearTimeout(debounceRef.current) } }, [])

  const [filters, setFilters] = useState({
    id_treasury_account: searchParams.get('id_treasury_account') ?? '',
    reconciliation_status: (searchParams.get('reconciliation_status') ?? 'all') as ReconciliationStatusFilter,
    from: searchParams.get('from') ?? '',
    to: searchParams.get('to') ?? '',
    sort_field: searchParams.get('sort_field') ?? 'date',
    sort_mode: (searchParams.get('sort_mode') ?? 'desc') as 'asc' | 'desc',
  })

  useEffect(() => {
    const params: Record<string, string> = {}
    if (filters.id_treasury_account) params.id_treasury_account = filters.id_treasury_account
    if (filters.reconciliation_status !== 'all') params.reconciliation_status = filters.reconciliation_status
    if (filters.from) params.from = filters.from
    if (filters.to) params.to = filters.to
    if (filters.sort_field !== 'date') params.sort_field = filters.sort_field
    if (filters.sort_mode !== 'desc') params.sort_mode = filters.sort_mode
    // Preserve q across filter changes
    if (urlQ) params.q = urlQ
    // Reset page to 1 when filters change — don't carry over stale page
    setSearchParams(params, { replace: true })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const loadTransactions = useCallback(async (showSpinner: boolean = false) => {
    if (!companyForUser) return

    if (showSpinner) setTransactions(null)

    try {
      const response = await BankStatementDA.getTransactions(companyForUser.id, {
        ...(filters.id_treasury_account && { id_treasury_account: filters.id_treasury_account }),
        ...(filters.reconciliation_status !== 'all' && { reconciliation_status: filters.reconciliation_status }),
        ...(filters.from && { from: filters.from }),
        ...(filters.to && { to: filters.to }),
        ...(filters.sort_field && { sort_field: filters.sort_field }),
        ...(filters.sort_mode && { sort_mode: filters.sort_mode }),
        ...(urlQ && { q: urlQ }),
        page: urlPage,
        limit: pagination.limit,
      })

      if (response.status === 200 || response.status === 304) {
        const payload = response.data ?? {}
        const items: BankTransaction[] = Array.isArray(payload.items) ? payload.items : []
        // Transitional: normalize _id → line_id until backend lot "convention id lignes" is deployed.
        // REMOVE this block once all responses expose `line_id` on every transaction.
        for (const tx of items) {
          if (!tx.line_id && (tx as any)._id) { tx.line_id = (tx as any)._id }
        }
        setTransactions(items)
        if (payload.pagination) {
          const newPages = payload.pagination.pages ?? 1
          setPagination(prev => ({...prev, total: payload.pagination.total ?? 0, pages: newPages}))
          // Clamp: if current page exceeds new total pages, go to last valid page
          if (urlPage > newPages && newPages >= 1) {
            setUrlPage(newPages)
          }
        }
      } else {
        setTransactions([])
      }
    } catch (err) {
      console.error('[BankTransactionList] load failed', err)
      setTransactions([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, filters, urlPage, urlQ, pagination.limit])

  useEffect(() => {
    loadTransactions(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, filters.id_treasury_account, filters.reconciliation_status, filters.from, filters.to, filters.sort_field, filters.sort_mode, urlPage, urlQ])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.treasuryAccounts, branch: lang.labels.bankTransactions}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Journals for manual entry ──
  const [journals, setJournals] = useState<Array<{ id: string; type: string; name: string }>>([])

  useEffect(() => {
    if (!companyForUser) return
    BookingJournalDA.getBookingJournals(companyForUser.id, '', '', '').then(res => {
      if (res.status === 200) {
        const data = res.data
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data?.booking_journals) ? data.booking_journals : (Array.isArray(data) ? data : []))
        setJournals(items.map((j: any) => ({ id: j.id ?? j._id, type: j.type ?? '', name: j.name ?? '' })))
      }
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  // ── Manual entry modal ──
  const [manualEntryTx, setManualEntryTx] = useState<BankTransaction | null>(null)

  const resolveTreasury = (tx: BankTransaction): { id: string; label: string; name: string; currency_code: string } => {
    const ta = (treasuryAccountsForCompany ?? []).find((a: TreasuryAccount) => a.id === tx.id_treasury_account)
    const accountingId = (ta as any)?.accounting_account?.id ?? ''
    const accountingLabel = (ta as any)?.accounting_account
      ? formatAccountingAccount((ta as any).accounting_account, companyForUser?.language)
      : (ta?.name ?? '')
    const currencyCode = (ta as any)?.accounting_account?.currency?.code ?? (ta as any)?.currency?.code ?? 'CHF'
    return { id: accountingId, label: accountingLabel, name: ta?.name ?? '', currency_code: currencyCode }
  }

  const handleOpenManualEntry = async (tx: BankTransaction) => {
    if (!requireTxLineId(tx)) return
    setManualEntryTx(tx)
    if (!validationAccounts && companyForUser) {
      try {
        const res = await AccountingAccountDA.getAccountingAccounts(companyForUser.id, [], [], [])
        if (res.status === 200) {
          setValidationAccounts(res.data.accounting_accounts ?? [])
        }
      } catch {}
    }
    // Load suggestions if not already loaded for this transaction
    const isAlreadyLoaded = matchTx && matchTx.line_id === tx.line_id && matchTx.id_bank_statement === tx.id_bank_statement
    if (!isAlreadyLoaded && companyForUser) {
      try {
        const sugRes = await BankStatementDA.getPaymentSuggestions(companyForUser.id, tx.id_bank_statement, tx.line_id)
        if (sugRes.status === 200) {
          const data = sugRes.data
          setPaymentSuggestions({
            unvalidated_candidates: Array.isArray(data?.unvalidated_candidates) ? data.unvalidated_candidates : [],
            payable_candidates: Array.isArray(data?.payable_candidates) ? data.payable_candidates : [],
            receivable_candidates: Array.isArray(data?.receivable_candidates) ? data.receivable_candidates : [],
          })
        }
      } catch {}
    }
    modalShow('modal-manual-entry')
  }

  const handleManualEntrySuccess = () => {
    setManualEntryTx(null)
    loadTransactions()
  }

  // ── Auto-match global ──
  const [autoMatching, setAutoMatching] = useState(false)

  const handleAutoMatchAll = async () => {
    if (!companyForUser) return
    setAutoMatching(true)
    try {
      const res = await BankStatementDA.autoMatchAll(companyForUser.id)
      if (res.status === 200) {
        setTimeout(() => loadTransactions(), 3000)
      }
    } finally {
      setAutoMatching(false)
    }
  }

  // ── Export ──
  const [exporting, setExporting] = useState(false)

  const handleExport = async (format: 'xlsx' | 'pdf') => {
    if (!companyForUser) return
    setExporting(true)
    try {
      const period = filters.from && filters.to ? `${filters.from}_${filters.to}` : ''
      const fallback = exportFallbackFilename(lang.labels.bankTransactions, companyForUser.name ?? '', period, format)
      const result = await BankStatementDA.exportTransactions(companyForUser.id, format, {
        ...(filters.id_treasury_account && { id_treasury_account: filters.id_treasury_account }),
        ...(filters.reconciliation_status !== 'all' && { reconciliation_status: filters.reconciliation_status }),
        ...(filters.from && { from: filters.from }),
        ...(filters.to && { to: filters.to }),
        ...(urlQ && { q: urlQ }),
        ...(filters.sort_field && { sort_field: filters.sort_field }),
        ...(filters.sort_mode && { sort_mode: filters.sort_mode }),
        lang: lang.code.toLowerCase(),
      }, fallback)

      if (result.blob && result.status === 200) {
        triggerBlobDownload(result.blob, result.filename ?? fallback)
      } else {
        GlobalSwal.fire({
          title: result.error ?? lang.labels.exportError,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt,
        })
      }
    } catch (error) {
      console.error('[BankTransactionList] export failed', error)
      GlobalSwal.fire({
        title: lang.labels.exportError,
        icon: 'error',
        confirmButtonText: lang.labels.okGotIt,
      })
    } finally {
      setExporting(false)
    }
  }

  // ── Per-line reconciliation modal ──
  const [matchTx, setMatchTx] = useState<BankTransaction | null>(null)
  const [matchCandidates, setMatchCandidates] = useState<MatchCandidate[]>([])
  const [matchLoading, setMatchLoading] = useState(false)
  const [paymentSuggestions, setPaymentSuggestions] = useState<{ unvalidated_candidates?: any[]; payable_candidates?: PayableCandidate[]; receivable_candidates?: PayableCandidate[] }>({})

  // Validate-and-reconcile state
  const [validationProviderInvoice, setValidationProviderInvoice] = useState<ProviderInvoice | null>(null)
  const [validationInvoice, setValidationInvoice] = useState<Invoice | null>(null)
  const [validationExpense, setValidationExpense] = useState<Expense | null>(null)
  const [validationIncomeAccounts, setValidationIncomeAccounts] = useState<AccountingAccount[] | null>(null)
  const [validationServices, setValidationServices] = useState<Service[] | null>(null)
  const [validationProducts, setValidationProducts] = useState<Product[] | null>(null)
  const [validationAccounts, setValidationAccounts] = useState<AccountingAccount[] | null>(null)
  const [validationTaxAccounts, setValidationTaxAccounts] = useState<AccountingAccount[] | null>(null)
  const [validationExpenseAccounts, setValidationExpenseAccounts] = useState<AccountingAccount[] | null>(null)
  const [validationSettings, setValidationSettings] = useState<Settings | null>(null)
  const [bankReconContext, setBankReconContext] = useState<{ id_bank_statement: string; id_bank_statement_line: string; bank_date: string; bank_amount: number; id_treasury_account: string } | null>(null)

  // Transitional: resolve line_id with _id fallback until backend lot "convention id lignes" is deployed.
  // REMOVE the _id fallback once all responses expose `line_id`.
  const requireTxLineId = (tx: BankTransaction): string | null => {
    const resolved = tx.line_id || (tx as any)._id
    if (!resolved) {
      console.error('[BankTransactionList] transaction has no line_id — action blocked', tx)
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' })
    }
    return resolved ?? null
  }

  const handleOpenMatch = async (tx: BankTransaction) => {
    if (!companyForUser) return
    if (!requireTxLineId(tx)) return

    setMatchTx(tx)
    setMatchCandidates([])
    setPaymentSuggestions({})
    setMatchLoading(true)
    modalShow('modal-bank-line-match')

    const [candRes, sugRes] = await Promise.allSettled([
      BankStatementDA.getLineCandidates(companyForUser.id, tx.id_bank_statement, tx.line_id),
      BankStatementDA.getPaymentSuggestions(companyForUser.id, tx.id_bank_statement, tx.line_id),
    ])

    if (candRes.status === 'fulfilled' && candRes.value.status === 200) {
      const data = candRes.value.data
      setMatchCandidates(Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []))
    }

    if (sugRes.status === 'fulfilled' && sugRes.value.status === 200) {
      const data = sugRes.value.data
      setPaymentSuggestions({
        unvalidated_candidates: Array.isArray(data?.unvalidated_candidates) ? data.unvalidated_candidates : [],
        payable_candidates: Array.isArray(data?.payable_candidates) ? data.payable_candidates : [],
        receivable_candidates: Array.isArray(data?.receivable_candidates) ? data.receivable_candidates : [],
      })
    }

    setMatchLoading(false)
  }

  const handleMatched = async (lineId: string, idEntry: string) => {
    if (!matchTx || !companyForUser) return
    const res = await BankStatementDA.matchLine(companyForUser.id, matchTx.id_bank_statement, lineId, idEntry)
    if (res.status === 200) {
      setMatchTx(null)
      loadTransactions()
    }
  }

  const handlePayInvoice = async (doc: PayableCandidate) => {
    if (!companyForUser || !matchTx) return
    const resolvedLineId = requireTxLineId(matchTx)
    if (!resolvedLineId) return

    try {
      const response = await BankStatementDA.payAndLinkInvoice(companyForUser.id, matchTx.id_bank_statement, resolvedLineId, {
        id_provider_invoice: doc.kind === 'provider-invoice' ? doc.id : undefined,
        id_invoice: doc.kind === 'invoice' ? doc.id : undefined,
        kind: doc.kind,
        amount: Math.abs(matchTx.amount),
        date: matchTx.date,
      })
      if (response.status === 200 || response.status === 201) {
        setMatchTx(null)
        setPaymentSuggestions({})
        modalHide('modal-bank-line-match')
        loadTransactions()
      }
    } catch (err) {
      console.error('[BankTransactionList] pay invoice failed', err)
    }
  }

  const handleValidateDocument = async (doc: UnvalidatedCandidate) => {
    if (!companyForUser || !matchTx) return
    const resolvedLineId = requireTxLineId(matchTx)
    if (!resolvedLineId) return

    // Build bank reconciliation context from current transaction
    setBankReconContext({
      id_bank_statement: matchTx.id_bank_statement,
      id_bank_statement_line: resolvedLineId,
      bank_date: matchTx.date,
      bank_amount: Math.abs(matchTx.amount),
      id_treasury_account: matchTx.id_treasury_account,
    })

    modalHide('modal-bank-line-match')

    try {
      const isExpense = doc.kind === 'expense'
      const isInvoice = doc.kind === 'invoice'

      const [docRes, allRes, taxRes, expRes, incomeRes, settingsRes, servicesRes, productsRes, vatRegimeRes, taxCodesRes] = await Promise.allSettled([
        isExpense
          ? ExpenseDA.getExpense(companyForUser.id, doc.id)
          : isInvoice
            ? InvoiceDA.getInvoice(companyForUser.id, doc.id)
            : ProviderInvoiceDA.getProviderInvoice(companyForUser.id, doc.id),
        AccountingAccountDA.getAccountingAccounts(companyForUser.id, [], [], []),
        AccountingAccountDA.getAccountingAccounts(companyForUser.id, ['1170', '1171'], [], []),
        AccountingAccountDA.getAccountingAccounts(companyForUser.id, [], ['4', '6'], []),
        AccountingAccountDA.getAccountingAccounts(companyForUser.id, [], ['3'], []),
        SettingsDA.getSettings(),
        isInvoice ? ServiceDA.getServices(companyForUser.id, '', '', '', '') : Promise.resolve(null),
        isInvoice ? ProductDA.getProducts(companyForUser.id, '', '', '', '') : Promise.resolve(null),
        AccountingAccountDA.getVatRegime(companyForUser.id),
        AccountingAccountDA.getTaxCodes(companyForUser.id),
      ])

      if (docRes.status === 'fulfilled' && docRes.value.status === 200) {
        const data = docRes.value.data?.data ?? docRes.value.data
        if (isExpense) {
          setValidationExpense(data)
          setValidationProviderInvoice(null)
          setValidationInvoice(null)
        } else if (isInvoice) {
          setValidationInvoice(data)
          setValidationProviderInvoice(null)
          setValidationExpense(null)
        } else {
          setValidationProviderInvoice(data)
          setValidationExpense(null)
          setValidationInvoice(null)
        }
      } else {
        console.error('[BankTransactionList] failed to load document for validation')
        return
      }

      // Accounts — extract like Purchase.tsx / Main.tsx: response.data.accounting_accounts
      if (allRes.status === 'fulfilled' && allRes.value.status === 200) {
        setValidationAccounts(allRes.value.data.accounting_accounts ?? [])
      }
      if (taxRes.status === 'fulfilled' && taxRes.value.status === 200) {
        setValidationTaxAccounts(taxRes.value.data.accounting_accounts ?? [])
      }
      if (expRes.status === 'fulfilled' && expRes.value.status === 200) {
        setValidationExpenseAccounts(expRes.value.data.accounting_accounts ?? [])
      }
      if (incomeRes.status === 'fulfilled' && incomeRes.value.status === 200) {
        setValidationIncomeAccounts(incomeRes.value.data.accounting_accounts ?? [])
      }
      if (isInvoice && servicesRes.status === 'fulfilled' && servicesRes.value && servicesRes.value.status === 200) {
        const sPayload = servicesRes.value.data ?? {}
        setValidationServices(Array.isArray(sPayload.items) ? sPayload.items : Array.isArray(sPayload) ? sPayload : Array.isArray(sPayload.services) ? sPayload.services : [])
      }
      if (isInvoice && productsRes.status === 'fulfilled' && productsRes.value && productsRes.value.status === 200) {
        const pPayload = productsRes.value.data ?? {}
        setValidationProducts(Array.isArray(pPayload.items) ? pPayload.items : Array.isArray(pPayload) ? pPayload : Array.isArray(pPayload.products) ? pPayload.products : [])
      }
      if (settingsRes.status === 'fulfilled' && settingsRes.value.status === 200) {
        const s = settingsRes.value.data?.data ?? settingsRes.value.data

        // Merge VAT regime overrides (same logic as Main.tsx loadSettingsForCompany)
        const vatOverrides = (vatRegimeRes.status === 'fulfilled' && vatRegimeRes.value && vatRegimeRes.value.status === 200 && vatRegimeRes.value.data)
          ? {
              subject_tax: vatRegimeRes.value.data.subject_tax,
              tax_method: vatRegimeRes.value.data.tax_method,
              tax_rate: vatRegimeRes.value.data.tax_rate,
              tax_declaration: vatRegimeRes.value.data.tax_declaration,
            }
          : {}
        const taxFees = (taxCodesRes.status === 'fulfilled' && taxCodesRes.value && taxCodesRes.value.status === 200 && Array.isArray(taxCodesRes.value.data))
          ? taxCodesRes.value.data
          : s.tax_fees ?? []

        setValidationSettings({ ...s, ...vatOverrides, tax_fees: taxFees })
      }

      setTimeout(() => {
        if (isExpense) {
          modalShow('modal-expense-update-validate')
        } else if (isInvoice) {
          modalShow('modal-invoice-update-validate')
        } else {
          modalShow('modal-provider-invoice-update-validate')
        }
      }, 400)
    } catch (err) {
      console.error('[BankTransactionList] validate document failed', err)
    }
  }

  const handleReconciled = async () => {
    setValidationProviderInvoice(null)
    setValidationInvoice(null)
    setValidationExpense(null)
    setValidationServices(null)
    setValidationProducts(null)
    setBankReconContext(null)
    loadTransactions()
  }

  // ── Unmatch / Delete entry from transactions ──
  const handleUnmatch = async (tx: BankTransaction) => {
    if (!companyForUser) return
    const lineId = requireTxLineId(tx)
    if (!lineId) return

    try {
      const res = await BankStatementDA.unmatchLine(companyForUser.id, tx.id_bank_statement, lineId)
      if (res.status === 200) {
        loadTransactions()
      }
    } catch (err) {
      console.error('[BankTransactionList] unmatch failed', err)
    }
  }

  const handleDeleteReconciliationEntry = async (tx: BankTransaction) => {
    if (!companyForUser) return
    const lineId = requireTxLineId(tx)
    if (!lineId || !tx.id_entry) return

    try {
      const res = await BankStatementDA.deleteReconciliationEntry(companyForUser.id, tx.id_bank_statement, lineId)
      if (res.status === 200) {
        loadTransactions()
      } else {
        console.error('[BankTransactionList] delete entry failed', res.data)
      }
    } catch (err) {
      console.error('[BankTransactionList] delete entry failed', err)
    }
  }

  const handleRestore = async (tx: BankTransaction) => {
    if (!companyForUser) return
    const lineId = requireTxLineId(tx)
    if (!lineId) return

    try {
      const res = await BankStatementDA.restoreLine(companyForUser.id, tx.id_bank_statement, lineId)
      if (res.status === 200) {
        loadTransactions()
      }
    } catch (err) {
      console.error('[BankTransactionList] restore failed', err)
    }
  }

  // Map BankTransaction to BankStatementLine for the modal
  const matchLineForModal: BankStatementLine | null = matchTx ? {
    id: matchTx.line_id,
    date: matchTx.date,
    amount: matchTx.amount,
    description: matchTx.description,
    reference: matchTx.reference,
    iban_counterpart: matchTx.iban_counterpart,
    reconciliation_status: matchTx.reconciliation_status,
    id_entry: matchTx.id_entry,
    id_invoice: matchTx.id_invoice,
    matched_at: matchTx.matched_at,
    matched_via: matchTx.matched_via,
    match_note: matchTx.match_note,
  } : null

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && treasuryAccountsForCompany
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label small text-muted">{lang.labels.treasuryAccount}</label>
                  <Form.Select bsPrefix="form-select form-select-sm" value={filters.id_treasury_account} onChange={(e) => { setFilters(prev => ({...prev, id_treasury_account: e.target.value})); setPagination(prev => ({...prev, page: 1})) }}>
                    <option value="">{lang.labels.allTreasuryAccounts}</option>
                    { treasuryAccountsForCompany.map((item: TreasuryAccount, index: number) => (
                      <option key={index} value={item.id}>{item.name}{item.iban ? ` — ${item.iban}` : ''}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small text-muted">{lang.labels.status}</label>
                  <Form.Select bsPrefix="form-select form-select-sm" value={filters.reconciliation_status} onChange={(e) => { setFilters(prev => ({...prev, reconciliation_status: e.target.value as ReconciliationStatusFilter})); setPagination(prev => ({...prev, page: 1})) }}>
                    <option value="all">{lang.labels.all}</option>
                    <option value="pending">{lang.labels.pending}</option>
                    <option value="matched">{lang.labels.matched}</option>
                    <option value="ignored">{lang.labels.ignored}</option>
                  </Form.Select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small text-muted">{lang.labels.dateFrom}</label>
                  <input type="date" className="form-control form-control-sm" value={filters.from} onChange={(e) => { setFilters(prev => ({...prev, from: e.target.value})); setPagination(prev => ({...prev, page: 1})) }} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small text-muted">{lang.labels.dateTo}</label>
                  <input type="date" className="form-control form-control-sm" value={filters.to} onChange={(e) => { setFilters(prev => ({...prev, to: e.target.value})); setPagination(prev => ({...prev, page: 1})) }} />
                </div>
                <div className="col-md-3">
                  <label className="form-label small text-muted">{lang.labels.search}</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={lang.labels.search}
                      value={searchText}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                    {searchText && (
                      <button
                        type="button"
                        className="btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y me-1"
                        onClick={handleSearchClear}
                      >
                        <i className="bi bi-x-lg fs-7 text-muted"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-3 d-flex align-items-end gap-2">
                  <button className="btn btn-sm btn-light" onClick={() => { setFilters({id_treasury_account: '', reconciliation_status: 'all', from: '', to: '', sort_field: 'date', sort_mode: 'desc'}); handleSearchClear() }}>
                    {lang.labels.resetFilters}
                  </button>
                  <button className="btn btn-sm btn-primary" onClick={handleAutoMatchAll} disabled={autoMatching}>
                    { autoMatching
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>{lang.labels.matching}</>
                      : lang.labels.autoMatchAll
                    }
                  </button>
                  <DropdownActions
                    label={exporting ? `${lang.labels.export}...` : lang.labels.export}
                    buttonClassName={`btn btn-sm btn-light-primary ${exporting ? 'disabled' : ''}`}
                    items={[
                      {
                        key: 'xlsx',
                        label: `${lang.labels.excel} (.xlsx)`,
                        icon: <i className="bi bi-file-earmark-spreadsheet fs-5"></i>,
                        onClick: () => handleExport('xlsx'),
                      },
                      {
                        key: 'pdf',
                        label: lang.labels.pdf,
                        icon: <i className="bi bi-file-earmark-pdf fs-5"></i>,
                        onClick: () => handleExport('pdf'),
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body p-0">
              <div className="dataTables_wrapper border border-2 rounded">
                <div className="table-responsive rounded">
                  <table className="table align-middle table-row-bordered gx-10 mb-2">
                    <thead className="bg-light border-bottom-2">
                      <tr className="text-dark text-uppercase fw-bolder fs-7">
                        <th className="min-w-100px text-start cursor-pointer user-select-none" onClick={() => setFilters(f => ({...f, sort_field: 'date', sort_mode: f.sort_field === 'date' && f.sort_mode === 'desc' ? 'asc' : 'desc'}))}>
                          {lang.labels.date}
                          {filters.sort_field === 'date' && <span className="ms-1">{filters.sort_mode === 'desc' ? '▼' : '▲'}</span>}
                        </th>
                        <th className="min-w-200px text-start">{lang.labels.description}</th>
                        <th className="min-w-100px text-start">{lang.labels.reference}</th>
                        <th className="min-w-120px text-start">{lang.labels.counterpartIban}</th>
                        <th className="min-w-100px text-end">{lang.labels.amount}</th>
                        <th className="min-w-80px text-start">{lang.labels.status}</th>
                        <th className="min-w-80px text-start">{lang.labels.matchedVia}</th>
                        <th className="min-w-120px"></th>
                      </tr>
                    </thead>
                    { transactions
                      ?
                      <>
                        { transactions.length > 0
                          ?
                          <tbody className="text-secondary fw-bold">
                            { transactions.map((tx, index) => (
                              <tr key={index} className="row-dashed">
                                <td className="text-nowrap">{dateFormat(lang.code, tx.date)}</td>
                                <td>
                                  <div className="fw-bold">{tx.description || '—'}</div>
                                  { tx.match_note &&
                                    <small className="text-muted">{tx.match_note}</small>
                                  }
                                </td>
                                <td><small className="text-muted font-monospace">{tx.reference ?? '—'}</small></td>
                                <td><small className="font-monospace">{tx.iban_counterpart ?? '—'}</small></td>
                                <td className={`text-end fw-bold ${tx.amount < 0 ? 'text-danger' : 'text-success'}`}>
                                  {formatMoney(tx.amount)}
                                </td>
                                <td>
                                  { tx.reconciliation_status === 'matched' &&
                                    <span className="badge badge-light-success">{lang.labels.matched}</span>
                                  }
                                  { tx.reconciliation_status === 'pending' &&
                                    <span className="badge badge-light-warning">{lang.labels.pending}</span>
                                  }
                                  { tx.reconciliation_status === 'ignored' &&
                                    <span className="badge badge-light-secondary">{lang.labels.ignored}</span>
                                  }
                                </td>
                                <td>
                                  { tx.matched_via &&
                                    <small className="text-muted">{tx.matched_via}</small>
                                  }
                                </td>
                                <td>
                                  <div className="d-flex gap-1">
                                    { tx.reconciliation_status === 'pending' &&
                                      <>
                                        <button className="btn btn-sm btn-light-primary" onClick={() => handleOpenMatch(tx)}>
                                          {lang.labels.reconcile}
                                        </button>
                                        <button className="btn btn-sm btn-light-info" onClick={() => handleOpenManualEntry(tx)}>
                                          {lang.labels.manualEntry ?? 'Manual entry'}
                                        </button>
                                      </>
                                    }
                                    { tx.reconciliation_status === 'matched' &&
                                      <>
                                        <button className="btn btn-sm btn-light-warning" onClick={() => handleUnmatch(tx)}>
                                          {lang.labels.unmatch}
                                        </button>
                                        <button className="btn btn-sm btn-light-danger" onClick={() => handleDeleteReconciliationEntry(tx)}>
                                          {lang.labels.deleteAccountingEntry}
                                        </button>
                                      </>
                                    }
                                    { tx.reconciliation_status === 'ignored' &&
                                      <button className="btn btn-sm btn-light-warning" onClick={() => handleRestore(tx)}>
                                        {lang.labels.restoreLine}
                                      </button>
                                    }
                                    <button className="btn btn-icon btn-sm btn-light-primary" onClick={() => navigate(`/main/treasury/bank-statement/view/${tx.id_bank_statement}`)} title={lang.labels.viewStatement}>
                                      <span className="svg-icon svg-icon-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                          <path d="M17.5 11H6.5C4 11 2 9 2 6.5C2 4 4 2 6.5 2H17.5C20 2 22 4 22 6.5C22 9 20 11 17.5 11ZM15 6.5C15 7.9 16.1 9 17.5 9C18.9 9 20 7.9 20 6.5C20 5.1 18.9 4 17.5 4C16.1 4 15 5.1 15 6.5Z" fill="black" />
                                          <path opacity="0.3" d="M17.5 22H6.5C4 22 2 20 2 17.5C2 15 4 13 6.5 13H17.5C20 13 22 15 22 17.5C22 20 20 22 17.5 22ZM6.5 15C5.1 15 4 16.1 4 17.5C4 18.9 5.1 20 6.5 20C7.9 20 9 18.9 9 17.5C9 16.1 7.9 15 6.5 15Z" fill="black" />
                                        </svg>
                                      </span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          :
                          <tbody>
                            <tr className="text-secondary fw-bold row-bordered">
                              <td colSpan={8} className="text-center">{lang.labels.noTransactionsFound}</td>
                            </tr>
                          </tbody>
                        }
                      </>
                      :
                      <tbody>
                        <tr>
                          <td colSpan={8} className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">{lang.labels.loading}</span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    }
                  </table>
                </div>
              </div>
            </div>
          </div>
          { pagination.pages > 1 &&
            <div className="d-flex justify-content-between align-items-center mt-4">
              <small className="text-muted">
                {pagination.total} {lang.labels.transactions}
              </small>
              <div className="d-flex align-items-center gap-3">
                <AppElementPagination pagination={{page: urlPage, limit: pagination.limit, total: pagination.total, pages: pagination.pages}} onPageChange={(p) => setUrlPage(p)} />
                <div className="d-flex align-items-center gap-1">
                  <input
                    className="form-control form-control-sm"
                    style={{width: '60px'}}
                    type="number"
                    min={1}
                    max={pagination.pages}
                    placeholder="#"
                    value={goToPage}
                    onChange={(e) => setGoToPage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const target = Math.max(1, Math.min(pagination.pages, parseInt(goToPage, 10) || 1))
                        setUrlPage(target)
                        setGoToPage('')
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          }

          <AppModalBankLineMatch
            id_company={companyForUser.id}
            id_statement={matchTx?.id_bank_statement ?? ''}
            line={matchLineForModal}
            candidates={matchCandidates}
            loading={matchLoading}
            onMatched={handleMatched}
            paymentSuggestions={paymentSuggestions}
            onValidateDocument={handleValidateDocument}
            onPayInvoice={handlePayInvoice}
            onManualEntry={matchTx ? () => handleOpenManualEntry(matchTx) : undefined}
          />

          <AppModalProviderInvoiceUpdateValidate
            companyForUser={companyForUser}
            settingsForCompany={validationSettings}
            accountingAccountsForCompany={validationAccounts}
            accountingTaxAccountsForCompany={validationTaxAccounts}
            accountingExpenseAccountsForCompany={validationExpenseAccounts}
            providerInvoice={validationProviderInvoice}
            setProviderInvoice={setValidationProviderInvoice}
            bankReconContext={bankReconContext}
            onReconciled={handleReconciled}
          />

          <AppModalInvoiceUpdateValidate
            companyForUser={companyForUser}
            settingsForCompany={validationSettings}
            accountingAccountsForCompany={validationAccounts}
            accountingIncomeAccountsForCompany={validationIncomeAccounts}
            servicesForCompany={validationServices}
            productsForCompany={validationProducts}
            invoice={validationInvoice}
            setInvoice={setValidationInvoice}
            bankReconContext={bankReconContext}
            onReconciled={handleReconciled}
          />

          <AppModalExpenseUpdateValidate
            companyForUser={companyForUser}
            settingsForCompany={validationSettings}
            accountingAccountsForCompany={validationAccounts}
            accountingTaxAccountsForCompany={validationTaxAccounts}
            accountingExpenseAccountsForCompany={validationExpenseAccounts}
            expense={validationExpense}
            setExpense={setValidationExpense}
            bankReconContext={bankReconContext}
            onReconciled={handleReconciled}
          />

          <AppModalManualEntry
            id_company={companyForUser.id}
            bankLine={{
              line_id: manualEntryTx?.line_id ?? '',
              id_bank_statement: manualEntryTx?.id_bank_statement ?? '',
              amount: manualEntryTx?.amount ?? 0,
              date: manualEntryTx?.date ?? '',
              description: manualEntryTx?.description ?? '',
              currency_code: manualEntryTx ? resolveTreasury(manualEntryTx).currency_code : undefined,
            }}
            treasuryAccountId={manualEntryTx ? resolveTreasury(manualEntryTx).id : ''}
            treasuryAccountLabel={manualEntryTx ? resolveTreasury(manualEntryTx).label : ''}
            treasuryName={manualEntryTx ? resolveTreasury(manualEntryTx).name : ''}
            accounts={validationAccounts ?? []}
            journals={journals}
            companyForUser={companyForUser}
            settingsForCompany={validationSettings}
            onSuccess={handleManualEntrySuccess}
            hasSuggestions={
              (paymentSuggestions.payable_candidates?.length ?? 0) > 0 ||
              (paymentSuggestions.receivable_candidates?.length ?? 0) > 0 ||
              (paymentSuggestions.unvalidated_candidates?.length ?? 0) > 0
            }
            onSwitchToMatch={manualEntryTx ? () => handleOpenMatch(manualEntryTx) : undefined}
          />
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default BankTransactionListPage;
